import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getFAQs, createFAQ, updateFAQ, deleteFAQ } from '../../api';
import { PageHeader, EmptyState, TableSkeleton } from '../../components/ui';
import Modal from '../../components/ui/Modal';
import { Plus, Pencil, Trash2, HelpCircle, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';

const defaultForm = { question_en: '', question_hi: '', answer_en: '', answer_hi: '', category: 'general', order: 0, isActive: true };

export default function FAQsPage() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState<any>(defaultForm);
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['admin-faqs'], queryFn: () => getFAQs().then((r) => r.data.data.faqs) });

  const saveMutation = useMutation({
    mutationFn: (d: any) => editing ? updateFAQ(editing._id, d) : createFAQ(d),
    onSuccess: () => { toast.success('FAQ saved'); qc.invalidateQueries({ queryKey: ['admin-faqs'] }); closeModal(); },
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFAQ(id),
    onSuccess: () => { toast.success('FAQ deleted'); qc.invalidateQueries({ queryKey: ['admin-faqs'] }); },
  });

  const openCreate = () => { setEditing(null); setForm(defaultForm); setModal(true); };
  const openEdit = (f: any) => {
    setEditing(f);
    setForm({ question_en: f.question?.en || '', question_hi: f.question?.hi || '', answer_en: f.answer?.en || '', answer_hi: f.answer?.hi || '', category: f.category, order: f.order, isActive: f.isActive });
    setModal(true);
  };
  const closeModal = () => { setModal(false); setEditing(null); setForm(defaultForm); };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate({ question: { en: form.question_en, hi: form.question_hi }, answer: { en: form.answer_en, hi: form.answer_hi }, category: form.category, order: Number(form.order), isActive: form.isActive });
  };

  const faqs = data || [];

  return (
    <div className="p-6">
      <PageHeader title="FAQs" subtitle="Manage frequently asked questions (bilingual)">
        <button onClick={openCreate} className="btn-primary"><Plus size={15} /> Add FAQ</button>
        <button onClick={() => qc.invalidateQueries({ queryKey: ['admin-faqs'] })} className="btn-secondary btn-sm"><RefreshCw size={14} /></button>
      </PageHeader>

      <div className="card overflow-hidden">
        {isLoading ? <div className="p-5"><TableSkeleton rows={5} cols={3} /></div> : faqs.length === 0 ? (
          <EmptyState message="No FAQs yet" icon={<HelpCircle size={32} />} />
        ) : (
          <div className="divide-y divide-gray-100">
            {faqs.map((f: any) => (
              <div key={f._id} className="hover:bg-gray-50 transition-colors">
                <div className="flex items-start gap-3 px-5 py-4 cursor-pointer" onClick={() => setExpanded(expanded === f._id ? null : f._id)}>
                  <button className="text-gray-400 mt-0.5 flex-shrink-0">
                    {expanded === f._id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 leading-tight">{f.question?.en}</p>
                    {f.question?.hi && <p className="text-xs text-gray-500 mt-0.5">{f.question.hi}</p>}
                    <div className="flex gap-2 mt-1">
                      <span className="badge badge-gray capitalize">{f.category}</span>
                      {!f.isActive && <span className="badge badge-red">Inactive</span>}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={(e) => { e.stopPropagation(); openEdit(f); }} className="p-1.5 rounded hover:bg-gray-200 text-blue-500"><Pencil size={13} /></button>
                    <button onClick={(e) => { e.stopPropagation(); if (window.confirm('Delete?')) deleteMutation.mutate(f._id); }} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 size={13} /></button>
                  </div>
                </div>
                {expanded === f._id && (
                  <div className="px-12 pb-4">
                    <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                      <div><p className="text-xs font-semibold text-gray-500 mb-1">English Answer</p><p className="text-sm text-gray-700">{f.answer?.en}</p></div>
                      {f.answer?.hi && <div><p className="text-xs font-semibold text-gray-500 mb-1">Hindi Answer</p><p className="text-sm text-gray-700">{f.answer.hi}</p></div>}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal open={modal} onClose={closeModal} title={editing ? 'Edit FAQ' : 'Add FAQ'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="block text-xs font-medium text-gray-700 mb-1">Question (English) *</label><input required className="input" value={form.question_en} onChange={(e) => setForm({ ...form, question_en: e.target.value })} /></div>
            <div className="col-span-2"><label className="block text-xs font-medium text-gray-700 mb-1">Question (Hindi)</label><input className="input" value={form.question_hi} onChange={(e) => setForm({ ...form, question_hi: e.target.value })} /></div>
            <div className="col-span-2"><label className="block text-xs font-medium text-gray-700 mb-1">Answer (English) *</label><textarea required rows={3} className="input resize-none" value={form.answer_en} onChange={(e) => setForm({ ...form, answer_en: e.target.value })} /></div>
            <div className="col-span-2"><label className="block text-xs font-medium text-gray-700 mb-1">Answer (Hindi)</label><textarea rows={3} className="input resize-none" value={form.answer_hi} onChange={(e) => setForm({ ...form, answer_hi: e.target.value })} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Category</label><input className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="general, consultation, payment…" /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Order</label><input type="number" className="input" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></div>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} /> Active</label>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={closeModal} className="btn-secondary flex-1 justify-center">Cancel</button>
            <button type="submit" disabled={saveMutation.isPending} className="btn-primary flex-1 justify-center">{saveMutation.isPending ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
